import React, { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import {
  CreditCard,
  Download,
  ArrowUpRight,
  FileText,
  ExternalLink,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  fetchEmsBilling,
  downloadEmsReceipt,
  viewEmsInvoice,
} from "../../Apis/EmsBilling";
import useAuthStore from "../../Zustand/Store/useAuthStore";

function formatEndDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatMoney(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return `₹${Number(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function DocPreviewModal({ doc, onClose, onDownload, downloading }) {
  if (!doc) return null;

  const isReceipt = doc.docType === "receipt";
  const title = isReceipt
    ? doc.receipt_doc_number || "Receipt"
    : doc.invoice_number || doc.receipt_number || "Invoice";
  const plan =
    `${doc.plan_name || doc.description || "IMS Plan"}` +
    (doc.billing_cycle ? ` · ${doc.billing_cycle}` : "");

  return (
    <div className="ems-inv-overlay" role="dialog" aria-modal="true">
      <div className="ems-inv-backdrop" onClick={onClose} />
      <div className="ems-inv-sheet">
        <div className="ems-inv-toolbar">
          <div>
            <p className="ems-billing-kicker">
              {isReceipt ? "Payment receipt" : "Tax invoice"}
            </p>
            <h2>{title}</h2>
          </div>
          <div className="ems-inv-toolbar-actions">
            <button
              type="button"
              className="ems-billing-download"
              onClick={() =>
                viewEmsInvoice(doc.client_payment_id, doc.docType || "invoice")
              }
            >
              <ExternalLink size={14} />
              Open full
            </button>
            <button
              type="button"
              className="ems-billing-download primary"
              disabled={downloading}
              onClick={() => onDownload(doc, doc.docType || "invoice")}
            >
              {downloading ? (
                <CircularProgress size={14} />
              ) : (
                <Download size={14} />
              )}
              Download
            </button>
            <button
              type="button"
              className="ems-inv-close"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ems-inv-body">
          <div className="ems-inv-meta">
            <div>
              <p className="ems-billing-label">Date</p>
              <p className="ems-billing-value">
                {formatEndDate(doc.payment_date)}
              </p>
            </div>
            <div>
              <p className="ems-billing-label">Status</p>
              <span className="ems-billing-pill active tiny">
                {String(doc.payment_status || "paid").toUpperCase()}
              </span>
            </div>
            <div>
              <p className="ems-billing-label">Payment ref</p>
              <p className="ems-billing-value mono-sm">
                {doc.payment_reference || "—"}
              </p>
            </div>
          </div>

          <div className="ems-inv-line">
            <div>
              <strong>{plan}</strong>
              <p>
                {isReceipt
                  ? "Payment confirmation for IMS subscription"
                  : "IMS subscription — tax invoice"}
              </p>
            </div>
          </div>

          {isReceipt ? (
            <div className="ems-inv-totals">
              <div className="ems-inv-row grand">
                <span>Amount paid</span>
                <span>{doc.amount_formatted}</span>
              </div>
            </div>
          ) : (
            <div className="ems-inv-totals">
              <div className="ems-inv-row">
                <span>Sub Total</span>
                <span>
                  {doc.subtotal != null
                    ? formatMoney(doc.subtotal)
                    : doc.amount_formatted}
                </span>
              </div>
              {doc.tax_mode === "cgst_sgst" ? (
                <>
                  <div className="ems-inv-row">
                    <span>
                      CGST (9%)
                      {doc.state_name ? ` · ${doc.state_name}` : ""}
                    </span>
                    <span>{formatMoney(doc.cgst)}</span>
                  </div>
                  <div className="ems-inv-row">
                    <span>SGST (9%)</span>
                    <span>{formatMoney(doc.sgst)}</span>
                  </div>
                </>
              ) : doc.gst_amount != null && Number(doc.gst_amount) > 0 ? (
                <div className="ems-inv-row">
                  <span>
                    GST / IGST (18%)
                    {doc.state_name ? ` · ${doc.state_name}` : ""}
                  </span>
                  <span>{formatMoney(doc.igst ?? doc.gst_amount)}</span>
                </div>
              ) : null}
              <div className="ems-inv-row grand">
                <span>Total paid</span>
                <span>{doc.amount_formatted}</span>
              </div>
            </div>
          )}

          <p className="ems-inv-note">
            {isReceipt
              ? "This receipt confirms payment was received. For Sub Total and GST break-up, open the Invoice."
              : "This tax invoice includes Sub Total and GST. A separate payment receipt is also available."}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function EmsBillingAccountPage() {
  const navigate = useNavigate();
  const { userData, login } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState("");
  const [subscription, setSubscription] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [clientName, setClientName] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchEmsBilling();
      setSubscription(data.subscription || null);
      setReceipts(Array.isArray(data.receipts) ? data.receipts : []);
      setClientName(data.client_name || "");
      if (userData && data.subscription) {
        login({
          ...userData,
          ems_subscription: data.subscription,
          billing_only: false,
          upgrade_required: Boolean(data.subscription?.upgrade_required),
        });
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to load billing."
      );
    } finally {
      setLoading(false);
    }
  }, [login, userData]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async (item, type = "invoice") => {
    if (!item?.client_payment_id) return;
    setDownloadingId(`${item.client_payment_id}-${type}`);
    try {
      const name =
        type === "receipt"
          ? item.receipt_doc_number || `RCP-EMS-${item.client_payment_id}`
          : item.invoice_number ||
            item.receipt_number ||
            `INV-EMS-${item.client_payment_id}`;
      await downloadEmsReceipt(item.client_payment_id, name, type);
      toast.success(
        type === "receipt" ? "Receipt downloaded." : "Invoice downloaded."
      );
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Unable to download document."
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = (item, type = "invoice") => {
    setSelectedDoc({ ...item, docType: type });
  };

  const planLabel =
    subscription?.plan_name ||
    subscription?.subscription_plan ||
    "No active plan";
  const status = String(subscription?.subscription_status || "").toLowerCase();
  const isTrial = Boolean(subscription?.is_trial) || status === "trial";
  const isActive = Boolean(subscription?.is_subscription_active);

  return (
    <div className="ems-billing-page">
      <div className="ems-billing-inner">
        <header className="ems-billing-header">
          <div>
            <p className="ems-billing-kicker">Account</p>
            <h1>Billing</h1>
            <p className="ems-billing-sub">
              Manage your InsSure plan and download invoices
              {clientName ? ` for ${clientName}` : ""}.
            </p>
          </div>
          <button
            type="button"
            className="ems-billing-upgrade"
            onClick={() => navigate("/billing/plans")}
          >
            <ArrowUpRight size={16} />
            {isActive && !isTrial ? "Change plan" : "Upgrade plan"}
          </button>
        </header>

        {loading ? (
          <div className="ems-billing-loading">
            <CircularProgress size={28} />
          </div>
        ) : error ? (
          <div className="ems-billing-error">{error}</div>
        ) : (
          <>
            <section className="ems-billing-card ems-billing-plan">
              <div className="ems-billing-plan-top">
                <div className="ems-billing-plan-icon">
                  <CreditCard size={20} />
                </div>
                <div>
                  <p className="ems-billing-label">Current plan</p>
                  <h2>{planLabel}</h2>
                </div>
                <span
                  className={`ems-billing-pill ${
                    isActive ? (isTrial ? "trial" : "active") : "expired"
                  }`}
                >
                  {isActive ? (isTrial ? "Trial" : "Active") : "Expired"}
                </span>
              </div>

              <div className="ems-billing-plan-grid">
                <div>
                  <p className="ems-billing-label">Billing cycle</p>
                  <p className="ems-billing-value">
                    {subscription?.billing_cycle
                      ? String(subscription.billing_cycle).replace(/^\w/, (c) =>
                          c.toUpperCase()
                        )
                      : isTrial
                        ? "Trial"
                        : "—"}
                  </p>
                </div>
                <div>
                  <p className="ems-billing-label">Seats</p>
                  <p className="ems-billing-value">
                    {subscription?.license_count
                      ? `Up to ${subscription.license_count}`
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="ems-billing-label">Started</p>
                  <p className="ems-billing-value">
                    {formatEndDate(subscription?.start_date)}
                  </p>
                </div>
                <div>
                  <p className="ems-billing-label">
                    {isActive ? "Renews / ends" : "Ended"}
                  </p>
                  <p className="ems-billing-value">
                    {formatEndDate(subscription?.end_date)}
                    {typeof subscription?.days_left === "number" &&
                    subscription.days_left >= 0
                      ? ` · ${subscription.days_left} days left`
                      : ""}
                  </p>
                </div>
              </div>
            </section>

            <section className="ems-billing-card">
              <div className="ems-billing-section-head">
                <h3>Invoices & receipts</h3>
                <p>
                  After each payment you get a tax invoice (Sub Total + GST) and
                  a payment receipt. View or download either anytime.
                </p>
              </div>

              {receipts.length === 0 ? (
                <div className="ems-billing-empty">
                  No invoices or receipts yet. After a successful payment, they
                  will appear here.
                </div>
              ) : (
                <ul className="ems-inv-list">
                  {receipts.map((r) => {
                    const invoiceId =
                      r.invoice_number || r.receipt_number || "Invoice";
                    const receiptId =
                      r.receipt_doc_number ||
                      `RCP-EMS-${String(r.client_payment_id).padStart(6, "0")}`;
                    return (
                      <li key={r.client_payment_id} className="ems-inv-item">
                        <div className="ems-inv-item-icon">
                          <FileText size={18} />
                        </div>
                        <div className="ems-inv-item-main">
                          <div className="ems-inv-id-row">
                            <button
                              type="button"
                              className="ems-inv-item-title"
                              onClick={() => handleView(r, "invoice")}
                            >
                              {invoiceId}
                            </button>
                            <span className="ems-inv-sep">·</span>
                            <button
                              type="button"
                              className="ems-inv-item-title soft"
                              onClick={() => handleView(r, "receipt")}
                            >
                              {receiptId}
                            </button>
                          </div>
                          <p>
                            {formatEndDate(r.payment_date)}
                            {" · "}
                            {r.plan_name || "IMS plan"}
                            {r.billing_cycle ? ` (${r.billing_cycle})` : ""}
                          </p>
                        </div>
                        <div className="ems-inv-item-amount">
                          <strong>{r.amount_formatted}</strong>
                          <span className="ems-billing-pill active tiny">
                            Paid
                          </span>
                        </div>
                        <div className="ems-inv-item-actions">
                          <button
                            type="button"
                            className="ems-billing-download"
                            onClick={() => handleView(r, "invoice")}
                          >
                            Invoice
                          </button>
                          <button
                            type="button"
                            className="ems-billing-download"
                            onClick={() => handleView(r, "receipt")}
                          >
                            Receipt
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}
      </div>

      <DocPreviewModal
        doc={selectedDoc}
        onClose={() => setSelectedDoc(null)}
        onDownload={handleDownload}
        downloading={
          selectedDoc &&
          downloadingId ===
            `${selectedDoc.client_payment_id}-${selectedDoc.docType || "invoice"}`
        }
      />
    </div>
  );
}
